import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import dayjs from 'dayjs';
import { ConversationList, ConversationListItem } from '../MarketingTab/MarketingTab.styled';

// Create a localStorage key for approved messages
const APPROVED_MESSAGES_STORAGE_KEY = 'annaims_approved_messages';

// Function to get approved messages from localStorage
const getApprovedMessagesFromStorage = () => {
  try {
    const storedData = localStorage.getItem(APPROVED_MESSAGES_STORAGE_KEY);
    if (storedData) {
      return new Set(JSON.parse(storedData));
    }
  } catch (err) {
    // Silent error handling
  }
  return new Set();
};

// Function to save approved messages to localStorage
const saveApprovedMessagesToStorage = (messageIds) => {
  try {
    localStorage.setItem(APPROVED_MESSAGES_STORAGE_KEY, JSON.stringify([...messageIds]));
  } catch (err) {
    // Silent error handling
  }
};

// Create a global variable to track approved messages across the app
window.ANNAIMS_APPROVED_MESSAGES = window.ANNAIMS_APPROVED_MESSAGES || getApprovedMessagesFromStorage();

// Create a context to share approval status across conversation switches
const ApprovedMessagesContext = React.createContext({
  approvedIds: window.ANNAIMS_APPROVED_MESSAGES,
  addApprovedMessage: (id) => {
    window.ANNAIMS_APPROVED_MESSAGES.add(id);
    saveApprovedMessagesToStorage(window.ANNAIMS_APPROVED_MESSAGES);
  }
});

export const ConversationListWithTabs = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  isLoading,
  searchQuery,
  onSearchChange
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [approvedMessageIds, setApprovedMessageIds] = useState(getApprovedMessagesFromStorage());

  // Initialize and persist approved message IDs across the entire conversation list
  useEffect(() => {
    if (!conversations) return;
    
    const storedApprovedIds = getApprovedMessagesFromStorage();
    
    // Merge any new approved messages from conversations with our stored set
    conversations.forEach(conv => {
      if (conv.messages) {
        conv.messages.forEach(msg => {
          // Add any message with SENT status to our approved set
          if (msg.status === 'SENT' || msg.status === 'APPROVED') {
            storedApprovedIds.add(msg.id);
          }
        });
      }
    });
    
    // Update our local state and global variable
    setApprovedMessageIds(storedApprovedIds);
    window.ANNAIMS_APPROVED_MESSAGES = storedApprovedIds;
    
    // Save to localStorage
    saveApprovedMessagesToStorage(storedApprovedIds);
  }, [conversations]);

  // Add function to add an approved message
  const addApprovedMessage = useCallback((messageId) => {
    setApprovedMessageIds(prev => {
      const newSet = new Set([...prev, messageId]);
      window.ANNAIMS_APPROVED_MESSAGES = newSet;
      saveApprovedMessagesToStorage(newSet);
      return newSet;
    });
  }, []);

  // This will force a re-render when the approved messages change
  useEffect(() => {
    // Add event listener for message approval events
    const handleMessageApproved = (event) => {
      // Force refresh of approved messages from localStorage
      const updatedApprovedIds = getApprovedMessagesFromStorage();
      setApprovedMessageIds(updatedApprovedIds);
      
      // Also update the global variable to ensure consistency
      window.ANNAIMS_APPROVED_MESSAGES = updatedApprovedIds;
    };
    
    window.addEventListener('annaims_message_approved', handleMessageApproved);
    
    return () => {
      window.removeEventListener('annaims_message_approved', handleMessageApproved);
    };
  }, []);

  // When a conversation is selected, update it to respect our approved message IDs
  const handleSelectConversation = useCallback((conversation) => {
    if (conversation.messages) {
      // Get most up-to-date approved messages 
      const currentApprovedIds = window.ANNAIMS_APPROVED_MESSAGES;
      
      // Update messages to respect our global approved state
      const updatedMessages = conversation.messages.map(msg => {
        if (currentApprovedIds.has(msg.id)) {
          return { 
            ...msg, 
            status: 'SENT',
            approvedAt: msg.approvedAt || new Date().toISOString()
          };
        }
        return msg;
      });
      
      // Create an updated conversation object with the fixed message statuses
      const updatedConversation = {
        ...conversation,
        messages: updatedMessages
      };
      
      onSelectConversation(updatedConversation);
    } else {
      onSelectConversation(conversation);
    }
  }, [onSelectConversation]);

  // Use a ref to track the current value of approvedMessageIds for the memoized pendingConversationsCount
  const approvedIdsRef = useRef(window.ANNAIMS_APPROVED_MESSAGES);
  
  // Update the ref whenever approvedMessageIds changes
  useEffect(() => {
    approvedIdsRef.current = window.ANNAIMS_APPROVED_MESSAGES;
  }, [approvedMessageIds]);

  // Make the pending count reactive to changes in the global approved messages
  const pendingConversationsCount = useMemo(() => {
    if (!conversations || !Array.isArray(conversations)) return 0;
    
    // Get fresh copy of the current approved IDs
    const currentApprovedIds = new Set([...approvedIdsRef.current]);
    
    // Count conversations with pending messages not in approved set
    return conversations.filter(conv => {
      if (!conv.messages) return false;
      return conv.messages.some(msg => 
        msg.status === 'PENDING_SEND' && !currentApprovedIds.has(msg.id)
      );
    }).length;
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, approvedIdsRef.current]); // Include the ref to make it reactive

  const hasContactMessage = useCallback((conversation) => {
    if (!conversation.messages) return false;
    return conversation.messages.some(msg => msg.role === 'CONTACT');
  }, []);

  const hasPendingMessages = useCallback((conversation) => {
    if (!conversation.messages) return false;
    // Check if any message is pending AND not in our approved set
    const currentApprovedIds = window.ANNAIMS_APPROVED_MESSAGES;
    return conversation.messages.some(msg => 
      msg.status === 'PENDING_SEND' && !currentApprovedIds.has(msg.id)
    );
  }, []);

  // Refresh the count when localStorage changes (for cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === APPROVED_MESSAGES_STORAGE_KEY) {
        // Force refresh of approved messages
        const newApprovedIds = getApprovedMessagesFromStorage();
        setApprovedMessageIds(newApprovedIds);
        window.ANNAIMS_APPROVED_MESSAGES = newApprovedIds;
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const filteredConversations = useMemo(() => {
    if (!conversations || !Array.isArray(conversations)) return [];
    
    let filtered = conversations;
    
    // Filter by search query if present
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(conv => {
        const contactName = (conv.contact_name || '').toLowerCase();
        const lastMessage = (conv.last_message || '').toLowerCase();
        const email = (conv.contact_email || '').toLowerCase();
        const phone = (conv.contact_phone || '').toLowerCase();

        return contactName.includes(query) ||
               lastMessage.includes(query) ||
               email.includes(query) ||
               phone.includes(query);
      });
    }

    // Filter by tab
    return filtered.filter(conv => {
      const hasContact = hasContactMessage(conv);
      return activeTab === 0 ? hasContact : !hasContact;
    });
  }, [conversations, searchQuery, activeTab, hasContactMessage]);

  // Create a context value for sharing approval state
  const approvedMessagesContextValue = useMemo(() => ({
    approvedIds: approvedMessageIds,
    addApprovedMessage
  }), [approvedMessageIds, addApprovedMessage]);

  return (
    <ApprovedMessagesContext.Provider value={approvedMessagesContextValue}>
      <ConversationList>
        <div className="search-box">
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              sx: {
                backgroundColor: '#F5F5F5',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: '#EEEEEE'
                },
                borderRadius: '8px',
                padding: '8px 12px'
              }
            }}
          />
        </div>

        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          px: 2,
          '& .MuiTabs-root': {
            minHeight: '48px',
          },
          '& .MuiTabs-flexContainer': {
            justifyContent: 'center'
          }
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: '200px',
                px: 2,
                py: 1.5,
                fontSize: '14px'
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-flex',
                  alignItems: 'center',
                  pr: 3 // Add padding to the right to accommodate the badge
                }}>
                  <Badge 
                    color="error" 
                    badgeContent={pendingConversationsCount}
                    invisible={pendingConversationsCount === 0}
                    sx={{ 
                      '& .MuiBadge-badge': {
                        position: 'absolute',
                        right: -24,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        padding: '0 6px',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(255, 77, 77, 0.4)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 6px rgba(255, 77, 77, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(255, 77, 77, 0)',
                          },
                        },
                      }
                    }}
                  >
                    Active Conversations
                  </Badge>
                </Box>
              } 
            />
            <Tab label="No Customer Reply" />
          </Tabs>
        </Box>

        {isLoading ? (
          <Box p={3} textAlign="center">
            <CircularProgress size={24} />
            <Typography color="textSecondary" style={{ marginTop: 16 }}>
              Loading conversations...
            </Typography>
          </Box>
        ) : (
          <>
            {filteredConversations.length > 0 ? (
              filteredConversations.map(conv => (
                <ConversationListItem
                  key={conv.id}
                  selected={conv.id === selectedConversation?.id}
                  onClick={() => handleSelectConversation(conv)}
                  hasPending={hasPendingMessages(conv)}
                >
                  <Typography className="contact-name">
                    {conv.contact_name || 'Unknown Contact'}
                  </Typography>
                  <Typography className="preview">
                    {conv.last_message || 'No messages'}
                  </Typography>
                  <div className="conversation-meta">
                    <Typography className="timestamp">
                      {dayjs(conv.last_updated).format("MM/DD/YYYY HH:mm")}
                    </Typography>
                  </div>
                </ConversationListItem>
              ))
            ) : (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">
                  {searchQuery.trim() ? 'No conversations found matching your search' : 'No conversations found'}
                </Typography>
              </Box>
            )}
          </>
        )}
      </ConversationList>
    </ApprovedMessagesContext.Provider>
  );
}; 

// Export the context so it can be used in other components
export { ApprovedMessagesContext }; 